import { useState, useEffect } from 'react';
import mosqueService from '../services/mosqueService';

const PAGE_SIZE = 12;

const SORT_TO_ORDERING = {
  newest: '-created_at',
  oldest: 'created_at',
  name: 'name',
  capacity: 'name',
};

function parseListResponse(data) {
  if (Array.isArray(data)) return data;
  if (data?.results && Array.isArray(data.results)) return data.results;
  return [];
}

/**
 * Loads paginated mosques for a society via GET /mosques/api/ (society + ordering + search).
 */
function useSocietyMosques({ societyId, currentPage, search, sort }) {
  const [mosques, setMosques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!societyId) {
      setMosques([]);
      setTotalCount(0);
      setError(null);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const ordering = SORT_TO_ORDERING[sort] || 'name';
        const params = {
          society: societyId,
          page: currentPage,
          page_size: PAGE_SIZE,
          ordering,
        };
        const q = (search || '').trim();
        if (q) params.search = q;
        const res = await mosqueService.getMosques(params);
        if (cancelled) return;
        const rows = parseListResponse(res);
        setMosques(rows);
        setTotalCount(typeof res?.count === 'number' ? res.count : rows.length);
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setMosques([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [societyId, currentPage, search, sort]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return {
    mosques,
    loading,
    error,
    totalCount,
    totalPages,
    pageSize: PAGE_SIZE,
  };
}

export default useSocietyMosques;
